//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import {
  EXCHANGE_RATE_ONE,
  ExchangeRateTypeEnum,
  ExchangeRateTypeToString,
  PoolStateToString,
  yupErc20WeiRequired,
  yupEthAddrRequired,
} from './Enums'
import { PoolBaseOverviewCTO, PoolBaseOverviewCTOSchema } from './PoolBaseCTO'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ZeroAddress } from 'ethers'

export const PoolDynamicOnlyOverviewCTOSchemaValues: Record<string, yup.Schema> = {}

export const PoolDynamicOverviewCTOSchema = yup
  .object({
    feeCollectorAddress: yupEthAddrRequired
      .label('Fee Collector Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'feeCollectorAddress',
          stringType: StringTypeEnum.ETH_ADDR,
          description: 'The smart-contract address for the Fee Collector. ',
        })
      ),
    closeOfDepositTime: yup
      .number()
      .required()
      .default(0)
      .label('Advance Cut-Off Time')
      .meta(
        toFieldMetaData({
          propName: 'closeOfDepositTime',
          description:
            'The cut-off time for loans in seconds from 0:00 UTC e.g. 64800 is 18:00 UTC. Any loans received after this time will be treated as if they were received the next business day.',
          stringType: StringTypeEnum.ETH_TIME_OF_DAY,
        })
      ),
    closeOfWithdrawTime: yup
      .number()
      .required()
      .default(0)
      .label('Withdrawal Request Cut-Off Time')
      .meta(
        toFieldMetaData({
          propName: 'closeOfWithdrawTime',
          description:
            'The cut-off time for withdrawal request in seconds from 0:00 UTC e.g. 64800 is 18:00 UTC. Any withdrawal requests received after this time will be treated as if they were received the next business day. ',
          stringType: StringTypeEnum.ETH_TIME_OF_DAY,
        })
      ),
    state: yup
      .number()
      .required()
      .default(0)
      .label('State')
      .meta(
        toFieldMetaData({
          propName: 'state',
          description: 'The State of the Loan Vault',
          stateToString: PoolStateToString,
        })
      ),

    totalAssetsDeposited: yupErc20WeiRequired
      .label('Total Assets Deposited')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsDeposited',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 13,
          description:
            'The total amount of assets deposited since vault inception. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    totalAssetsWithdrawn: yupErc20WeiRequired
      .label('Total Assets Withdrawn')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsWithdrawn',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 14,
          description:
            'The total amount of assets withdrawn since vault inception. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    exchangeRate: yupErc20WeiRequired
      .label('Exchange Rate')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 15,
          description:
            'The current exchange rate at which Vault Tokens and Assets will be exchanged. The Exchange Rate increases by the Daily Interest Rate each day. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),

    exchangeRateAtSetDay: yupErc20WeiRequired
      .label('Exchange Rate At Set Day')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtSetDay',
          description:
            'The exchange rate at which Vault Tokens and Assets were exchanged on the set day. The Exchange Rate uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.ETH_DAY_NUMBER,
        })
      ),

    exchangeRateChangeRate: yupErc20WeiRequired
      .label('Exchange Rate Change Rate')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateChangeRate',
          description:
            'The rate at which the Exchange Rate changes per compounding period. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),

    exchangeRateCompoundingRate: yupErc20WeiRequired
      .label('Exchange Rate Compounding Rate')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateCompoundingRate',
          description:
            'The rate applied during each compounding interval to calculate the Exchange Rate. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),
    exchangeRateAtMaturity: yupErc20WeiRequired
      .label('Exchange Rate At Maturity')
      .required()
      .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtMaturity',
          description:
            'The final exchange rate at which Vault Tokens and Assets will be exchanged at maturity. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),
    exchangeRateSetDay: yup
      .number()
      .label('Exchange Rate Set Day')
      .required()
      .default(0)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateSetDay',
          description: 'Day the exchange rate was set',
          stringType: StringTypeEnum.ETH_DAY_NUMBER,
        })
      ),
    exchangeRateMaturityDay: yup
      .number()
      .label('Exchange Rate Maturity Day')
      .required()
      .default(0)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateMaturityDay',
          description: 'The day on which the vault reaches maturity and the final exchange rate is applied.',
          stringType: StringTypeEnum.ETH_DAY_NUMBER,
        })
      ),
    exchangeRateType: yup
      .number()
      .oneOf(Object.values(ExchangeRateTypeEnum).filter((v) => typeof v === 'number') as number[])
      .label('Exchange Rate Type')
      .defined()
      .default(ExchangeRateTypeEnum.Unset)
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateType',
          description:
            'The exchange rate calculation method being used by the vault. Options include Dynamic (1), Linear (2), Compounding (3), and Term (4)',
          stateToString: ExchangeRateTypeToString,
        })
      ),
    totalInterestAccrued: yupErc20WeiRequired
      .label('Total Interest Accrued')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalInterestAccrued',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 12,
          description:
            'The total amount of interest accrued to date. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalShares: yupErc20WeiRequired
      .label('Total Vault Tokens Outstanding')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalShares',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 16,
          description:
            'The total number of vault tokens outstanding. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),

    totalAssets: yupErc20WeiRequired
      .label('Total Assets')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssets',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 11,
          description:
            'The total amount of assets. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    interestRate: yupErc20WeiRequired
      .label('Interest Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'interestRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 10,
          description:
            'The current annualized interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
          stringType: StringTypeEnum.APR,
        })
      ),
    indicativeInterestRate: yupErc20WeiRequired
      .label('Indicative Interest Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'indicativeInterestRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 18,
          description: 'The target rate of return for a dynamic vault',
          stringType: StringTypeEnum.APR,
        })
      ),
    collateralRate: yupErc20WeiRequired
      .label('Collateral Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'collateralRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 19,
          description:
            'The rate of return for the underlying collateral, as defined in the Master Loan Agreement. Only used in vaults with a Dynamic exchange rate.',
          stringType: StringTypeEnum.APR,
        })
      ),
  })
  .concat(PoolBaseOverviewCTOSchema)
  .label('PoolDynamicOverviewCTO')
  .meta(toFieldMetaData({ propName: 'PoolDynamicOverviewCTO', description: 'PoolDynamicOverviewCTOSchema' }))

export interface PoolDynamicOverviewCTO extends PoolBaseOverviewCTO {
  feeCollectorAddress: EthAddr
  exchangeRateType: ExchangeRateTypeEnum
  closeOfDepositTime: number
  closeOfWithdrawTime: number
  state: number
  totalAssetsDeposited: ERC20Wei
  totalAssetsWithdrawn: ERC20Wei

  exchangeRate: ERC20Wei
  exchangeRateAtSetDay: ERC20Wei
  exchangeRateSetDay: number
  exchangeRateChangeRate: ERC20Wei
  exchangeRateCompoundingRate: ERC20Wei
  exchangeRateAtMaturity: ERC20Wei
  exchangeRateMaturityDay: number

  totalInterestAccrued: ERC20Wei
  totalShares: ERC20Wei
  totalAssets: ERC20Wei
  interestRate: ERC20Wei

  indicativeInterestRate: ERC20Wei
  collateralRate: ERC20Wei
}

type PoolDynamicOverviewCTOInfer = InferType<typeof PoolDynamicOverviewCTOSchema>
export const _testAPoolTermOverview: PoolDynamicOverviewCTO = {} as unknown as PoolDynamicOverviewCTOInfer
export const _testBPoolTermOverview: PoolDynamicOverviewCTOInfer = {} as unknown as PoolDynamicOverviewCTO
